import { Descriptions, Flex, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { GenericIngredientFragment } from "../gql/generated/graphqlSchema.tsx";
import {
  foodCategoryLocalizedName,
  genericIngredientLocalizedBaseName,
  genericIngredientLocalizedName,
  genericIngredientLocalizedProperties,
} from "../utils/genericIngredient.ts";
import { title } from "../styles/shared.styles.ts";
import { AllergenTagList } from "./AllergenTagList.tsx";
import { NutritionInfo } from "./NutritionInfo.tsx";

// t("ingredients.sources.COFID")
// t("ingredients.sources.CNF")
const sourceInfo: Record<string, { url: string; labelKey: "ingredients.sources.COFID" | "ingredients.sources.CNF" }> = {
  COFID: {
    url: "https://www.gov.uk/government/publications/composition-of-foods-integrated-dataset-cofid",
    labelKey: "ingredients.sources.COFID",
  },
  CNF: {
    url: "https://food-nutrition.canada.ca/cnf-fce/",
    labelKey: "ingredients.sources.CNF",
  },
};

export const GenericIngredientDetails = ({ ingredient }: { readonly ingredient: GenericIngredientFragment }) => {
  const { t, i18n } = useTranslation();
  const baseName = genericIngredientLocalizedBaseName(ingredient, i18n.language);
  const properties = genericIngredientLocalizedProperties(ingredient, i18n.language);
  const category = foodCategoryLocalizedName(ingredient.foodCategory, i18n.language);
  const source = sourceInfo[ingredient.source];

  return (
    <Flex gap="middle" vertical>
      <Flex gap={4} vertical>
        <Space align="center" size="small" wrap>
          <Typography.Title className={title} level={4}>
            {baseName ?? genericIngredientLocalizedName(ingredient, i18n.language)}
          </Typography.Title>
          {category != null ? <Tag color="blue">{category}</Tag> : null}
        </Space>

        {properties != null && properties.length > 0 ? (
          <Typography.Text type="secondary">{properties}</Typography.Text>
        ) : null}
      </Flex>

      <NutritionInfo nutritionProfile={ingredient.nutritionProfile} />

      <AllergenTagList allergens={ingredient.allergens} />

      <Descriptions
        bordered
        column={1}
        size="small"
        styles={{
          header: {
            marginBottom: "0.5rem",
          },
          title: {
            fontSize: "0.9rem",
          },
        }}
        title={t("ingredients.sourceData")}
      >
        <Descriptions.Item label={t("ingredients.originalName")}>{ingredient.nameEn}</Descriptions.Item>
        {ingredient.description != null && ingredient.description.length > 0 ? (
          <Descriptions.Item label={t("ingredients.description")}>{ingredient.description}</Descriptions.Item>
        ) : null}
        {ingredient.sourceId != null ? (
          <Descriptions.Item label={t("ingredients.sourceId")}>{ingredient.sourceId}</Descriptions.Item>
        ) : null}
        {source != null ? (
          <Descriptions.Item label={t("ingredients.source")}>
            <Typography.Link href={source.url} target="_blank">
              {t(source.labelKey)}
            </Typography.Link>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </Flex>
  );
};
